import { COLUMNS } from './columns';
import { isBackgroundClick } from './row-click';
import { NATURAL_DIRECTION, type PostingListItem, type Sort, type SortDirection } from './types';

/**
 * The results as a table — the same rows the cards show, in the same order, from the same query.
 *
 * The table renders whatever the server returned; it does NOT sort client-side. Sorting a keyset
 * page in the browser would reorder twenty rows out of two hundred and present the result as the
 * ordering of the whole search, which is the most convincing kind of wrong. Clicking a header
 * changes the query and refetches, exactly as the sort control does.
 *
 * It scrolls inside its own container. Seven columns do not fit a laptop beside a 240px sidebar,
 * and a table that widens the PAGE takes the filters and the status bar sideways with it.
 *
 * THE WHOLE ROW OPENS THE POSTING, as the whole card does. The title cell is one of seven, and
 * having to land on it to open a row makes six-sevenths of a wide row inert — the reader is looking
 * at the deadline column when they decide. The title stays a real button: it is the keyboard path,
 * and the row is deliberately given no `role` or tab stop of its own, which would announce seven
 * columns as a single button and double the tab stops in the table.
 */

export interface ResultsTableProps {
  items: PostingListItem[];
  /** The ordering actually in effect — resolved, never the raw nullable choice. */
  sort: Sort;
  /** The direction actually in effect, resolved the same way. */
  direction: SortDirection;
  onSort: (sort: Sort, direction: SortDirection) => void;
  onOpen: (item: PostingListItem) => void;
  /** Frozen by the landing example; omitted in the product, where the clock is the real one. */
  now?: number;
}

export function ResultsTable({ items, sort, direction, onSort, onOpen, now }: ResultsTableProps) {
  return (
    <div className="tenders-tablewrap">
      <table className="tenders-table">
        <thead>
          <tr>
            {COLUMNS.map((column) => {
              // `relevance` is nobody's column, so during a ranked search NO header is marked —
              // which is the honest answer, and the reason this is a lookup rather than a boolean.
              const active = column.sort !== null && column.sort === sort;
              // An inactive header advertises the direction it WOULD apply — its key's natural one.
              // An active one flips, so the same click that sorted a column can unsort it the other
              // way. Setting the key alone made a second click a no-op, which reads as broken.
              const shown =
                column.sort === null || column.sort === 'relevance'
                  ? undefined
                  : active
                    ? direction
                    : NATURAL_DIRECTION[column.sort];
              const next: SortDirection | undefined =
                shown === undefined ? undefined : active ? (direction === 'asc' ? 'desc' : 'asc') : shown;

              return (
                <th
                  key={column.key}
                  scope="col"
                  className={column.className}
                  // Exactly one column may carry aria-sort. A screen reader announces every marked
                  // column as "sorted", so marking two describes a table that cannot exist.
                  aria-sort={active ? (direction === 'asc' ? 'ascending' : 'descending') : undefined}
                >
                  {column.sort === null ? (
                    column.label
                  ) : (
                    <button
                      type="button"
                      className="tenders-table__sort"
                      // Named in full, because "מועד הגשה" alone read out of the header context is
                      // a column name, not an action.
                      // The label says what the NEXT click does, not what the column is. On an
                      // active header that is the reversal, which is the whole point of the toggle.
                      aria-label={
                        active
                          ? `מיון לפי ${column.label}, ${next === 'asc' ? 'בסדר עולה' : 'בסדר יורד'}`
                          : `מיון לפי ${column.label}`
                      }
                      onClick={() => onSort(column.sort as Sort, next as SortDirection)}
                    >
                      {column.label}
                      <span className="tenders-table__arrow" aria-hidden="true">
                        {active ? (direction === 'asc' ? '▲' : '▼') : '↕'}
                      </span>
                    </button>
                  )}
                </th>
              );
            })}
          </tr>
        </thead>

        <tbody>
          {items.map((item) => (
            // Same key as the card list: the stored id is `<slug>:<siteId>` and neither half is
            // unique alone.
            <tr
              key={`${item.slug}:${item.siteId}`}
              onClick={(event) => {
                if (isBackgroundClick(event)) onOpen(item);
              }}
            >
              {COLUMNS.map((column) => (
                <td key={column.key} className={column.className}>
                  {column.cell(item, { onOpen, now })}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
